import PermissionRoute from './PermissionRoute';

/**
 * Helper function to wrap a component with PermissionRoute
 * @param {React.ReactNode} component - Component to wrap
 * @param {string} menuKey - Menu key for permission check
 * @param {string} action - Permission action (default: 'read')
 * @returns {React.ReactNode}
 */
export const withPermission = (component, menuKey, action = 'read') => {
  return (
    <PermissionRoute menuKey={menuKey} action={action}>
      {component}
    </PermissionRoute>
  );
};

/**
 * Permission Helper
 * Maps route paths to menu keys for permission checking
 * 
 * NOTE: routeMenuKeyMap is now dynamically generated from API response
 * in MenuProvider (hooks/useMenuPermissions.jsx). The mapping is built
 * automatically when user menus are fetched, extracting URL and key from
 * each menu item (including nested children).
 * 
 * This file only contains the getMenuKeyForRoute function which uses
 * the dynamic mapping from MenuContext.
 */

/**
 * Gets the menu key for a given route path
 * @param {string} path - The current route path
 * @param {Object} routeMenuKeyMap - Dynamic route map from MenuContext
 * @returns {string|null} - The menu key or null if not found
 */
export const getMenuKeyForRoute = (path, routeMenuKeyMap = {}) => {
  // 1. Try exact match first
  if (routeMenuKeyMap[path]) {
    return routeMenuKeyMap[path];
  }

  // 2. Try normalizing path - replace numeric IDs with :id pattern
  const normalizedPath = path.replace(/\/\d+/g, '/:id');
  if (normalizedPath !== path && routeMenuKeyMap[normalizedPath]) {
    return routeMenuKeyMap[normalizedPath];
  }

  // 3. Try pattern matching with regex for dynamic routes (e.g., /path/:id)
  for (const [routePath, menuKey] of Object.entries(routeMenuKeyMap)) {
    if (routePath.includes(':')) {
      // Convert route pattern to regex (e.g., /path/:id → /path/[^/]+)
      const pattern = routePath.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      
      if (regex.test(path)) {
        return menuKey;
      }
    }
  }

  // 4. Try parent path fallback (for nested routes without specific mapping)
  const segments = path.split('/').filter(Boolean);
  for (let i = segments.length - 1; i > 0; i--) {
    const parentPath = '/' + segments.slice(0, i).join('/');
    if (routeMenuKeyMap[parentPath]) {
      return routeMenuKeyMap[parentPath];
    }
  }

  return null;
};
